import { lazy, memo, ReactNode, Suspense } from 'react'
import { cn } from '@/lib/utils'

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const Streamdown: any = lazy(() =>
  import('streamdown').then((mod) => ({ default: mod.Streamdown })),
)

/**
 * Override streamdown's default components to use plain HTML tags.
 * This lets the app's Tailwind base typography (defined in web-styles/tailwind.config.ts) apply
 * instead of streamdown's hardcoded classes.
 *
 * Lists, code, tables, links, and images keep streamdown's defaults since those
 * provide necessary structure (list-disc, syntax highlighting, etc.).
 */
const baseComponents = {
  h1: ({ children }: { children?: ReactNode }) => <h1>{children}</h1>,
  h2: ({ children }: { children?: ReactNode }) => <h2>{children}</h2>,
  h3: ({ children }: { children?: ReactNode }) => <h3>{children}</h3>,
  h4: ({ children }: { children?: ReactNode }) => <h4>{children}</h4>,
  h5: ({ children }: { children?: ReactNode }) => <h5>{children}</h5>,
  h6: ({ children }: { children?: ReactNode }) => <h6>{children}</h6>,
  p: ({ children }: { children?: ReactNode }) => <p>{children}</p>,
  strong: ({ children }: { children?: ReactNode }) => (
    <strong className="font-semibold">{children}</strong>
  ),
  hr: () => <hr />,
  th: ({ children }: { children?: ReactNode }) => (
    <th className="min-w-[6ch] whitespace-nowrap px-4 py-2 text-left text-sm font-semibold">
      {children}
    </th>
  ),
  td: ({ children }: { children?: ReactNode }) => (
    <td className="min-w-[6ch] px-4 py-2 text-sm">{children}</td>
  ),
}

const compactComponents = {
  ...baseComponents,
  th: ({ children }: { children?: ReactNode }) => (
    <th className="min-w-[6ch] whitespace-nowrap px-2 py-1.5 text-left text-xs font-semibold">
      {children}
    </th>
  ),
  td: ({ children }: { children?: ReactNode }) => (
    <td className="min-w-[6ch] px-2 py-1 text-xs">{children}</td>
  ),
}

const BASE_CLASSES =
  'size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_ol]:ml-4 [&_ol]:list-decimal [&_ul]:ml-4 [&_ul]:list-disc'

interface ResponseProps {
  className?: string
  compact?: boolean
  children?: ReactNode
}

export const Response = memo(
  ({ className, compact, ...props }: ResponseProps) => (
    <Suspense fallback={null}>
      <Streamdown
        key={compact ? 'compact' : 'default'}
        className={cn(BASE_CLASSES, className)}
        components={compact ? compactComponents : baseComponents}
        {...props}
      />
    </Suspense>
  ),
  (prevProps, nextProps) =>
    prevProps.children === nextProps.children && prevProps.compact === nextProps.compact,
)

Response.displayName = 'Response'
